import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";

import Button from "../button/index.js";
import Forms from "../forms/index.js";
import Icons from "../../icons/index.js";

import styles from "./styles.module.scss";

const daysOfWeek = ["S", "M", "T", "W", "T", "F", "S"];

const months = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December"
];

const isDateInRange = (day, year, month, startDate, endDate) => {
  const date = new Date(year, month, day);
  return (
    startDate &&
    ((endDate && date >= startDate && date <= endDate) ||
      (!endDate && date.getTime() === startDate.getTime()))
  );
};

const selectDate = (
  day,
  year,
  month,
  startDate,
  endDate,
  setStartDate,
  setEndDate
) => {
  const newDate = new Date(year, month, day);
  if (!startDate || (startDate && endDate)) {
    setStartDate(newDate);
    setEndDate(null); // Reset end date if starting selection over
  } else if (startDate && !endDate) {
    if (newDate < startDate) {
      setEndDate(startDate);
      setStartDate(newDate);
    } else {
      setEndDate(newDate);
    }
  }
};

const Datepicker = (props) => {
  let initialDate = props.initialDate;
  initialDate.setHours(0, 0, 0, 0);

  const [year, setYear] = useState(initialDate.getFullYear());
  const [month, setMonth] = useState(initialDate.getMonth());
  const [startDate, setStartDate] = useState(initialDate);
  const [endDate, setEndDate] = useState(initialDate);

  const daysInMonth = useMemo(
    () => new Date(year, month + 1, 0).getDate(),
    [year, month]
  );
  const startDayOfMonth = useMemo(
    () => new Date(year, month).getDay(),
    [year, month]
  );

  useEffect(() => {
    props.onChange([startDate, endDate]);
  }, [startDate, endDate]);

  const years = useMemo(
    () =>
      Array.from(
        new Array(100),
        (val, index) => new Date().getFullYear() - index
      ),
    []
  );

  const handleSelectDate = useCallback(
    (day) => {
      selectDate(
        day,
        year,
        month,
        startDate,
        endDate,
        setStartDate,
        setEndDate
      );
    },
    [year, month, startDate, endDate]
  );

  const handleIsDateInRange = useCallback(
    (day) => {
      return isDateInRange(day, year, month, startDate, endDate);
    },
    [year, month, startDate, endDate]
  );

  const navigateMonth = useCallback(
    (direction) => {
      if (direction === "next") {
        if (month === 11) {
          setMonth(0);
          setYear(year + 1);
        } else {
          setMonth(month + 1);
        }
      } else if (direction === "prev") {
        if (month === 0) {
          setMonth(11);
          setYear(year - 1);
        } else {
          setMonth(month - 1);
        }
      }
    },
    [month, year]
  );

  return (
    <div className={styles.datepicker}>
      <div className={styles.datepickerNavigation}>
        <Button
          className={styles.datepickerNavigationButton}
          icon={<Icons.ArrowLeftIcon />}
          onClick={() => navigateMonth("prev")}
          size="small"
        />
        <Forms
          type="select"
          options={months.map((name, index) => ({
            label: `${name}`,
            value: index
          }))}
          onChange={(e) => setMonth(Number(e.target.value))}
          value={month}
        />
        <Forms
          type="select"
          options={years.map((year, index) => ({
            label: `${year}`,
            value: year
          }))}
          onChange={(e) => setYear(Number(e.target.value))}
          value={year}
        />
        <Button
          className={styles.datepickerNavigationButton}
          icon={<Icons.ArrowRightIcon />}
          onClick={() => navigateMonth("next")}
          size="small"
        />
      </div>
      <div className={styles.datepickerCalendar}>
        {daysOfWeek.map((day, index) => (
          <div key={index} className={styles.datepickerCalendarDayOfWeek}>
            {day}
          </div>
        ))}
        {Array.from({ length: startDayOfMonth }).map((_, index) => (
          <div
            className={styles.datepickerCalendarEmptyCell}
            key={`empty-${index}`}
          ></div>
        ))}
        {Array.from({ length: daysInMonth }).map((_, index) => {
          const day = index + 1;
          return (
            <Button
              className={styles.datepickerCalendarButton}
              label={`${day}`}
              key={day}
              onClick={() => handleSelectDate(day)}
              selected={handleIsDateInRange(day)}
              size="small"
            />
          );
        })}
      </div>
    </div>
  );
};

Datepicker.defaultProps = {
  onChange: () => {},
  initialDate: new Date()
};

Datepicker.propTypes = {
  onChange: PropTypes.func,
  initialDate: PropTypes.instanceOf(Date)
};

export default Datepicker;
